// クライアント側 Web Push ヘルパー（購読/解除/状態・端末判定）。
import { api } from "../api";

export function isIOS(): boolean {
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
}
export function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent || "");
}
// ホーム画面から起動中（PWA standalone）か
export function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}
export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}
export function permissionState(): NotificationPermission | "unsupported" {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function getVapidKey(): Promise<string | null> {
  try {
    const r = await api.get<{ key: string | null; configured: boolean }>("/push/vapid-public-key");
    return r.configured ? r.key : null;
  } catch { return null; }
}

export type EnableResult = { ok: true } | { ok: false; reason: "unsupported" | "denied" | "default" | "not-configured" | "error" };

// 通知許可 → 購読 → サーバ保存
export async function enablePush(): Promise<EnableResult> {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "denied" : "default" };
    const key = await getVapidKey();
    if (!key) return { ok: false, reason: "not-configured" };
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(key) as BufferSource });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: sub.endpoint, keys: json.keys });
    return { ok: true };
  } catch { return { ok: false, reason: "error" }; }
}

export async function disablePush(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch { /* noop */ }
      try { await sub.unsubscribe(); } catch { /* noop */ }
    }
  } catch { /* noop */ }
}

export async function isSubscribed(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return !!(await reg.pushManager.getSubscription());
  } catch { return false; }
}

// アプリアイコンの数字バッジ（Badging API）を未読数に同期。対応端末（iOS16.4+/Android等）のみ動作。
export async function syncAppBadge(count: number): Promise<void> {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0 && nav.setAppBadge) await nav.setAppBadge(count);
    else if (nav.clearAppBadge) await nav.clearAppBadge();
  } catch { /* 非対応環境は無視 */ }
}
